import React, {useEffect, useMemo, useRef, useState} from "react"
import { graphql, useStaticQuery, navigate } from "gatsby"
import Cookies from "js-cookie"
import { toast } from "react-toastify"
import { Helmet } from "react-helmet";

import OffsetAnchor from "../OffsetAnchor"
import ToastBody from "../ToastBody"

import styles from "./ApplicationList.module.scss"
import useTranslation from "../../hooks/useTranslation"
import createHtml from "../../helpers/createHtml"

async function readFile(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.onload = () => resolve(fileReader.result)
    fileReader.onerror = err => reject(err)

    fileReader.readAsDataURL(file)
  })
}

function extractFileName(path) {
  return path.split("\\").pop().split("/").pop()
}

function submitApply(
  portalId,
  formId,
  position,
  name,
  email,
  linkedIn,
  message,
  cvFileName = "",
  cvFile = null
) {
  const isBrowser = typeof window !== "undefined"
  const hutk = isBrowser ? Cookies.get("hubspotutk") : null
  const pageUri = isBrowser ? window.location.href : null
  const pageName = isBrowser ? document.title : null
  const hubspotBearerToken = process.env.HUBSPOT_BEARER_TOKEN;

  let body = {
    hsForm: {
      submittedAt: Date.now(),
      fields: [
        {
          name: "firstname",
          value: name,
        },
        {
          name: "email",
          value: email,
        },
        {
          name: "linkedinbio2",
          value: linkedIn,
        },
        {
          name: "TICKET.subject",
          value: `Job application: ${position}`,
        },
        {
          name: "TICKET.content",
          value: message,
        },
      ],
      context: {
        hutk,
        pageUri,
        pageName,
      },
    },
  }
  if (cvFile) {
    body = {
      ...body,
      cvFile: {
        name: cvFileName,
        data: cvFile,
      },
    }
  }
  
  return fetch("/.netlify/functions/apply-job", {
    method: "post",
    body: JSON.stringify(body),
    headers: new Headers({
      "Content-Type": "application/json",
      "authorization": `Bearer ${hubspotBearerToken}`,
      "Accept": "application/json, application/xml, text/plain, text/html, *.*",
    }),
  })
}

const ApplicationList = ({ language, appList, selected }) => {
  const { t } = useTranslation("joinUs")

  const langAppList = useMemo(
      () => appList.filter(item => item.language === language),
      [appList, language]
  );

  const fileInputRef = useRef()
  const infoRef = useRef()
  const formRef = useRef()

  const [cvFile, setCVFile] = useState()
  const [cvFileName, setCVFileName] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [linkedIn, setLinkedIn] = useState("")
  const [message, setMessage] = useState("")

  const { site } = useStaticQuery(graphql`
    query AppListData {
      site {
        siteMetadata {
          hubspotId
          applyFormId
        }
      }
    }
  `)

  const selectedApp = useMemo(() => {
    if (!selected)
      return undefined;

    return appList.find(item => (item.language === selected.language) && (item.slug === selected.slug));
  })

  useEffect(() => {
    if (selected && infoRef) {
      infoRef.current.scrollIntoView();
    }
  }, [selected, infoRef]);

  const handleUploadCVClick = e => {
    e.preventDefault()

    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleUploadCVFileChange = async e => {
    setCVFile(e.target.files[0])
    setCVFileName(e.target.value)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const portalId = site.siteMetadata.hubspotId
    const formId = site.siteMetadata.applyFormId

    try {
      let fileBase64 = null
      let fileName = extractFileName(cvFileName)
      if (cvFile) {
        fileBase64 = await readFile(cvFile)
      }

      const res = await submitApply(
        portalId,
        formId,
        selectedApp.title,
        name,
        email,
        linkedIn,
        message,
        fileName,
        fileBase64
      )
      if (res.status === 200 || res.status === 201) {
        setCVFile(undefined)
        setCVFileName("")
        setName("")
        setEmail("")
        setLinkedIn("")
        setMessage("")

        toast.success(<ToastBody text={t("appList.toast.thankYou")} />)
      } else {
        toast.error(<ToastBody text={t("appList.toast.valueError")} />)
      }
    } catch (err) {
      toast.error(<ToastBody text={t("appList.toast.requestError")} />)
    }
  }

  return (
    <div>
      <div className={styles.list}>
        {langAppList?.map((item, index) => (
          <div key={`app_${index}`} className={styles.list__item}>
            <div className={styles.list__item__title}>{item.title}</div>
            <div className={styles.list__item__descr}>{item.description}</div>
            <button
              className={styles.list__item__readMore}
              onClick={() => navigate(`/join-us/vacancy/${item.language}/${item.slug}`)}
              disabled={selected && item.slug === selected.slug}
            >
              Read more
            </button>
          </div>
        ))}
      </div>
      <OffsetAnchor anchorRef={infoRef} />
      {selectedApp !== undefined && (
        <>
          {selectedApp.tags && (
            <Helmet>
              <meta name="keywords" content={selectedApp.tags.join(", ")}/>
            </Helmet>
          )}
          <div className={styles.application}>
            <div className={styles.application__title}>
              {selectedApp.title}
            </div>
            <div
              className={styles.application__text}
              dangerouslySetInnerHTML={createHtml(selectedApp.text)}
            />
          </div>
          <div className={styles.appForm}>
            <div className={styles.appForm__title}>APPLY</div>
            <form
              className={styles.appForm__body}
              name="ApplyForm"
              ref={formRef}
            >
              <button
                className={styles.appForm__body__uploadBtn}
                onClick={handleUploadCVClick}
              >
                {t("appList.form.uploadCV")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf, .doc, .docx, .txt"
                name="cv"
                className={styles.appForm__body__uploadInput}
                onChange={handleUploadCVFileChange}
              />
              <input
                name="name"
                placeholder={t("appList.form.name")}
                value={name}
                onChange={e => setName(e.target.value)}
              />
              <input
                name="email"
                placeholder={t("appList.form.email")}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <input
                name="linkedin"
                placeholder={t("appList.form.linkedIn")}
                value={linkedIn}
                onChange={e => setLinkedIn(e.target.value)}
              />
              <textarea
                name="message"
                rows={4}
                placeholder={t("appList.form.message")}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
              <input
                type="submit"
                className={styles.appForm__body__applyBtn}
                value={t("appList.form.submit")}
                onClick={handleSubmit}
              />
            </form>
          </div>
        </>
      )}
    </div>
  )
}

export default ApplicationList
